import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, orderBy, onSnapshot, where, limit, startAfter, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Shield, Search, Filter, Calendar, ChevronDown, ChevronLeft, ChevronRight, Download, Clock, User, FileText, RefreshCw } from 'lucide-react';
import { AUDIT_ACTIONS } from '../utils/auditLog';

const ACTION_CATEGORIES = {
  shifts: ['shift_created', 'shift_assigned', 'shift_updated', 'shift_cancelled', 'shift_deleted', 'shift_block_created', 'shift_block_updated', 'shift_block_deleted'],
  rotas: ['rota_created', 'rota_published', 'rota_updated', 'rota_unpublished'],
  swaps: ['swap_requested', 'swap_approved', 'swap_rejected'],
  leave: ['leave_requested', 'leave_approved', 'leave_rejected'],
  ai: ['ai_suggestion_accepted', 'ai_suggestion_rejected', 'ai_auto_assign'],
  compliance: ['compliance_override', 'compliance_warning_acknowledged', 'fatigue_warning_acknowledged', 'fatigue_override'],
  templates: ['template_created', 'template_updated', 'template_deleted', 'template_applied'],
  settings: ['settings_updated', 'pay_rules_updated', 'sector_config_updated', 'role_hierarchy_updated', 'role_substitution'],
};

const ACTION_COLORS = {
  created: 'bg-green-500/20 text-green-400',
  published: 'bg-blue-500/20 text-blue-400',
  updated: 'bg-amber-500/20 text-amber-400',
  deleted: 'bg-red-500/20 text-red-400',
  cancelled: 'bg-red-500/20 text-red-400',
  approved: 'bg-emerald-500/20 text-emerald-400',
  rejected: 'bg-rose-500/20 text-rose-400',
  requested: 'bg-indigo-500/20 text-indigo-400',
  accepted: 'bg-green-500/20 text-green-400',
  override: 'bg-orange-500/20 text-orange-400',
  acknowledged: 'bg-cyan-500/20 text-cyan-400',
  assigned: 'bg-blue-500/20 text-blue-400',
  applied: 'bg-purple-500/20 text-purple-400',
  unpublished: 'bg-gray-500/20 text-gray-400',
};

function getActionColor(action) {
  for (const [key, cls] of Object.entries(ACTION_COLORS)) {
    if (action?.includes(key)) return cls;
  }
  return 'bg-gray-500/20 text-gray-400';
}

const PAGE_SIZE = 50;

const prettifyLabel = (value = '') =>
  String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

function formatPrimitiveValue(value, t) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? t('common.yes', 'Yes') : t('common.no', 'No');
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString();
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    return prettifyLabel(value);
  }
  return String(value);
}

function renderStructuredValue(value, t, depth = 0) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-500">—</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-500">—</span>;
    return (
      <div className="space-y-2">
        {value.map((item, index) => (
          <div key={index} className="rounded bg-white/5 p-2">
            {renderStructuredValue(item, t, depth + 1)}
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([, nestedValue]) => nestedValue !== undefined);
    if (entries.length === 0) return <span className="text-gray-500">—</span>;

    return (
      <div className="space-y-2">
        {entries.map(([key, nestedValue]) => (
          <div key={key} className="rounded bg-white/5 p-2">
            <p className="text-[10px] uppercase tracking-wide text-gray-500">{prettifyLabel(key)}</p>
            <div className="mt-1 text-gray-200">
              {typeof nestedValue === 'object'
                ? renderStructuredValue(nestedValue, t, depth + 1)
                : <span>{formatPrimitiveValue(nestedValue, t)}</span>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <span>{formatPrimitiveValue(value, t)}</span>;
}

export default function AuditLogViewer({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!companyId) return;

    const q = query(
      collection(db, 'companies', companyId, 'shiftsAuditLog'),
      orderBy('timestamp', 'desc'),
      limit(500)
    );

    const unsub = onSnapshot(q, snap => {
      setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));

    return unsub;
  }, [companyId]);

  const filtered = useMemo(() => {
    let list = logs;
    if (categoryFilter !== 'all') {
      const categoryActions = ACTION_CATEGORIES[categoryFilter] || [];
      list = list.filter(l => categoryActions.includes(l.action));
    }
    if (actionFilter !== 'all') {
      list = list.filter(l => l.action === actionFilter);
    }
    if (dateFrom) {
      list = list.filter(l => l.timestamp >= dateFrom);
    }
    if (dateTo) {
      list = list.filter(l => l.timestamp <= dateTo + 'T23:59:59');
    }
    if (userFilter) {
      list = list.filter(l =>
        (l.performedByName || '').toLowerCase().includes(userFilter.toLowerCase()) ||
        (l.performedBy || '').toLowerCase().includes(userFilter.toLowerCase())
      );
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(l =>
        (l.action || '').toLowerCase().includes(q) ||
        (l.performedByName || '').toLowerCase().includes(q) ||
        (l.targetId || '').toLowerCase().includes(q) ||
        JSON.stringify(l.metadata || {}).toLowerCase().includes(q)
      );
    }
    return list;
  }, [logs, categoryFilter, actionFilter, dateFrom, dateTo, userFilter, searchTerm]);

  const uniqueActions = [...new Set(logs.map(l => l.action))].sort();
  const uniqueUsers = [...new Set(logs.filter(l => l.performedByName).map(l => l.performedByName))].sort();
  const getActionLabel = action => action ? prettifyLabel(action) : '—';
  const getTargetLabel = log => log.targetType ? `${prettifyLabel(log.targetType)}: ${log.targetId || '—'}` : '—';

  const exportCSV = () => {
    let csv = 'Timestamp,Action,Performed By,Target ID,Target Type,AI Related,Details\n';
    filtered.forEach(l => {
      csv += `"${l.timestamp}","${getActionLabel(l.action)}","${l.performedByName || l.performedBy || ''}","${l.targetId || ''}","${prettifyLabel(l.targetType || '')}","${l.aiRelated || false}","${JSON.stringify(l.metadata || {}).replace(/"/g, '""')}"\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Stats
  const todayLogs = logs.filter(l => l.timestamp?.startsWith(new Date().toISOString().split('T')[0]));
  const aiLogs = logs.filter(l => l.aiRelated);
  const warningReviewLogs = logs.filter(l => l.action?.includes('override'));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="text-primary" size={24} />
            {t('auditLog.title', 'Audit Log')}
          </h1>
          <p className="text-sm text-gray-400 mt-1">{t('auditLog.subtitle', 'Complete activity trail for your organisation')}</p>
        </div>
        <button onClick={exportCSV}
          className="flex items-center gap-1.5 bg-green-600/20 hover:bg-green-600/30 text-green-400 px-3 py-2 rounded-lg text-sm font-medium transition">
          <Download size={14} /> {t('auditLog.export', 'Export CSV')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
          <FileText size={18} className="mx-auto text-primary mb-1" />
          <p className="text-xl font-bold text-white">{logs.length}</p>
          <p className="text-xs text-gray-400">{t('auditLog.totalEntries', 'Total Entries')}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
          <Clock size={18} className="mx-auto text-green-400 mb-1" />
          <p className="text-xl font-bold text-white">{todayLogs.length}</p>
          <p className="text-xs text-gray-400">{t('auditLog.today', 'Today')}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
          <RefreshCw size={18} className="mx-auto text-purple-400 mb-1" />
          <p className="text-xl font-bold text-white">{aiLogs.length}</p>
          <p className="text-xs text-gray-400">{t('auditLog.aiActions', 'AI Actions')}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
          <Shield size={18} className="mx-auto text-amber-400 mb-1" />
          <p className="text-xl font-bold text-white">{warningReviewLogs.length}</p>
          <p className="text-xs text-gray-400">{t('auditLog.warningReviews', 'Warning Reviews')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder={t('auditLog.search', 'Search audit log...')}
              className="w-full pl-10 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-400 focus:ring-2 focus:ring-primary" />
          </div>
          <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setActionFilter('all'); }}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white [color-scheme:dark]">
            <option value="all" className="bg-slate-700">{t('auditLog.allCategories', 'All Categories')}</option>
            {Object.keys(ACTION_CATEGORIES).map(cat => (
              <option key={cat} value={cat} className="bg-slate-700">{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
            ))}
          </select>
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white [color-scheme:dark]">
            <option value="all" className="bg-slate-700">{t('auditLog.allActions', 'All Actions')}</option>
            {uniqueActions.map(a => (
              <option key={a} value={a} className="bg-slate-700">{getActionLabel(a)}</option>
            ))}
          </select>
          <input type="text" value={userFilter} onChange={e => setUserFilter(e.target.value)}
            placeholder={t('auditLog.filterByUser', 'Filter by user...')}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:ring-2 focus:ring-primary min-w-[140px]" />
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-white [color-scheme:dark]" />
          <span className="text-gray-400 text-sm self-center">–</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-white [color-scheme:dark]" />
        </div>
        <p className="text-xs text-gray-400 mt-2">{t('auditLog.showing', 'Showing')} {filtered.length} {t('auditLog.of', 'of')} {logs.length} {t('auditLog.entries', 'entries')}</p>
      </div>

      {/* Log Entries */}
      <div className="space-y-1">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Shield size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">{t('auditLog.noEntries', 'No audit log entries found')}</p>
            <p className="text-sm mt-1">{t('auditLog.adjustFilters', 'Try adjusting your filters')}</p>
          </div>
        ) : (
          filtered.map(log => (
            <div key={log.id}
              className={`bg-white/5 border border-white/10 rounded-lg overflow-hidden transition ${expandedId === log.id ? 'ring-1 ring-primary/30' : ''}`}>
              <div
                onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/5 transition"
              >
                {/* Timestamp */}
                <div className="flex-shrink-0 w-36 text-xs text-gray-400">
                  {log.timestamp ? new Date(log.timestamp).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  }) : '—'}
                </div>

                {/* Action badge */}
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${getActionColor(log.action)}`}>
                  {getActionLabel(log.action)}
                </span>

                {/* Performer */}
                <div className="flex items-center gap-1 flex-shrink-0 min-w-[120px]">
                  <User size={12} className="text-gray-500" />
                  <span className="text-xs text-gray-300 truncate">{log.performedByName || log.performedBy?.slice(0, 8) || '—'}</span>
                </div>

                {/* Target */}
                {log.targetType && (
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {prettifyLabel(log.targetType)}: {log.targetId?.slice(0, 12) || '—'}
                  </span>
                )}

                {/* AI badge */}
                {log.aiRelated && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 flex-shrink-0">AI</span>
                )}

                <ChevronDown size={14} className={`ml-auto text-gray-500 transition ${expandedId === log.id ? 'rotate-180' : ''}`} />
              </div>

              {/* Expanded details */}
              {expandedId === log.id && (
                <div className="px-4 py-3 bg-white/5 border-t border-white/10">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500">{t('auditLog.action', 'Action')}</p>
                      <p className="text-white">{getActionLabel(log.action)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">{t('auditLog.timestamp', 'Timestamp')}</p>
                      <p className="text-white">{log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">{t('auditLog.performedBy', 'Performed By')}</p>
                      <p className="text-white">{log.performedByName || log.performedBy || '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">{t('auditLog.target', 'Target')}</p>
                      <p className="text-white">{getTargetLabel(log)}</p>
                    </div>
                    {log.previousValue && (
                      <div className="col-span-2">
                        <p className="text-gray-500">{t('auditLog.previousValue', 'Previous Value')}</p>
                        <div className="text-amber-300 bg-white/5 rounded p-2 mt-1 overflow-x-auto text-[11px]">
                          {renderStructuredValue(log.previousValue, t)}
                        </div>
                      </div>
                    )}
                    {log.newValue && (
                      <div className="col-span-2">
                        <p className="text-gray-500">{t('auditLog.newValue', 'New Value')}</p>
                        <div className="text-green-300 bg-white/5 rounded p-2 mt-1 overflow-x-auto text-[11px]">
                          {renderStructuredValue(log.newValue, t)}
                        </div>
                      </div>
                    )}
                    {log.metadata && Object.keys(log.metadata).length > 0 && (
                      <div className="col-span-2">
                        <p className="text-gray-500">{t('auditLog.metadata', 'Metadata')}</p>
                        <div className="text-gray-300 bg-white/5 rounded p-2 mt-1 overflow-x-auto text-[11px]">
                          {renderStructuredValue(log.metadata, t)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
